/**
 * Die Zusammenfuehrung einer JTL-Bestellung mit ihrer Hub-Zeile - der reine Rechenteil von
 * `api/jtlBestellungen.ts`, dort bis zum KI-Zugang (Stufe 1) beheimatet. Verschoben, weil der Server
 * (`backend/src/ki/auswertung/frontend/`) dieselbe Zusammenfuehrung faehrt wie die Oberflaeche und
 * deshalb eine zeichengleiche Kopie dieses Moduls braucht; `api/jtlBestellungen.ts` fuehrt alle
 * Namen als Re-Export weiter, kein Aufrufer musste umziehen.
 *
 * Der Inhalt ist UNVERAENDERT verschoben (Typen, Kennungshelfer, FOC-Vorbelegung, `zusammenfuehren`,
 * `jtlBestellungenZusammenfuehren`). Die Begruendung der Regeln - „WARUM ES DIESE DATEI GIBT" und
 * vor allem „WELCHE SEITE GEWINNT" (bei jedem Feld, das beide Seiten fuehren koennten, gewinnt JTL) -
 * steht im Kopfkommentar von `api/jtlBestellungen.ts`; wo die Kommentare unten „siehe Kopfkommentar"
 * oder „siehe WELCHE SEITE GEWINNT ... oben" sagen, ist DIESER gemeint (ebenso die Hinweise „unten"
 * auf `jtlBestellkopfAendern` und die uebrigen Schreibwege, die dort geblieben sind).
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): nur `.ts`-Importe innerhalb von
 * `frontend/src`, kein `react`, kein `import.meta`, keine Browser-Globals.
 */
import { istManuellerArtikel } from "./manuelleArtikel.ts";
import type { Order, OrderItem } from "./types.ts";

/** Eine Position, wie `GET /api/jtl/orders` sie liefert (backend/src/jtl/typen.ts). */
export interface JtlPositionRoh {
  kLieferantenBestellungPos: number;
  kArtikel: number | null;
  artikelNummer: string | null;
  /** Die Artikelbezeichnung am Beleg (`tLieferantenBestellungPos.cName`) - siehe `alsOrderItem`. */
  name: string | null;
  menge: number | null;
  ekNetto: number | null;
  mwstProzent: number | null;
  mengeGeliefert: number | null;
  anzahlOffen: number | null;
  lieferdatum: string | null;
  /**
   * `fEKNetto` ist AUSDRUECKLICH 0 - unterscheidet sich von einem ungepflegten (`NULL`) EK, das
   * `ekNetto ?? 0` im Frontend ebenfalls als 0 zeigt. Kommt fertig aus dem Backend
   * (`istFocKandidat`, `jtl/bestellungen.ts:222`), hier nur durchgereicht. Siehe
   * `berechneFocVorbelegung` unten.
   */
  focKandidat: boolean;
  /**
   * Freitext-Hinweis der Position (`cHinweis`, `JtlBestellPosition.hinweis`). Optional, weil
   * Snapshot-Bestellungen den Schluessel gar nicht fuehren - `alsOrderItem` macht daraus `null`.
   */
  hinweis?: string | null;
  /** JTL-Sortierreihenfolge der Position (`nSort`) - nur durchgereicht, siehe Kopfkommentar. */
  sortierung: number | null;
  /** Artikelbezeichnung BEIM LIEFERANTEN (`cLieferantenBezeichnung`) - siehe Kopfkommentar. */
  lieferantenBezeichnung: string | null;
  /** Artikelnummer BEIM LIEFERANTEN (`cLieferantenArtNr`) - siehe Kopfkommentar. */
  lieferantenArtNr: string | null;
  /**
   * Ob der Artikel in JTL ein Bild hat (`backend/src/jtl/bilder.ts#leseHatBildFuerArtikel`, seit
   * Auftrag "hatBild vorab mitgeben", 2026-08-27) - `false` bei einer Freiposition (`kArtikel:
   * null`), sonst das Ergebnis der Batch-Abfrage der Route.
   */
  hatBild: boolean;
  /**
   * Ob der Artikel neu ist, in JTL also noch keine Lagerbewegung hat (`backend/src/jtl/neueArtikel.ts`).
   * Liste und Einzelbestellung (`GET /api/jtl/orders[/:k]`) fuehren das Feld an jeder Position; die
   * JTL-Schreibantworten NICHT - daher optional. Fehlt es, uebernimmt `alsOrderItem` den bisherigen Wert
   * (siehe `neuerArtikelNachKArtikel`).
   */
  neuerArtikel?: boolean;
}

/** Eine Bestellung, wie `GET /api/jtl/orders` sie liefert. */
export interface JtlBestellungRoh {
  kLieferantenBestellung: number;
  kLieferant: number | null;
  lieferantFirma: string | null;
  eigeneBestellnummer: string | null;
  jtlStatus: number | null;
  jtlStatusLabel: string | null;
  erstellt: string | null;
  lieferdatum: string | null;
  bestaetigt: boolean | null;
  waehrung: string | null;
  fremdbelegnummer: string | null;
  /** Ziel-Warenlager der Bestellung (`kLager`) - siehe Kopfkommentar. */
  kLager: number | null;
  /** Klartext des Ziel-Warenlagers, aus dem LEFT JOIN auf `tWarenLager` - siehe Kopfkommentar. */
  lagerName: string | null;
  /** Referenz auf den ausloesenden Verkaufsauftrag (`cBezugsAuftragsNummer`) - siehe Kopfkommentar. */
  bezugsAuftragsNummer: string | null;
  /** Interner Freitext an der Bestellung (`cInternerKommentar`) - siehe Kopfkommentar. */
  internerKommentar: string | null;
  /**
   * Freitext fuer den LIEFERANTEN unterhalb der Artikelpositionen (`cDruckAnmerkung`) - seit Auftrag
   * "Liefertermin auf echtes JTL-Feld umstellen" (2026-08-26) Teil des DTO. Bisher (Nachtrag 2/3 zu
   * "Bestellfelder nach Anlage sperren") eine hub-eigene Spalte (`order_extras.delivery_term_text`,
   * "einmal befuellbar, dann gesperrt") - diese Spalte ist mit demselben Auftrag entfernt.
   */
  deliveryTermText: string | null;
  /** Waehrungsumrechnungsfaktor des Belegs (`fFaktor`) - siehe Kopfkommentar. */
  faktor: number | null;
  /**
   * Der aktuelle `rowversion`-Stand des Belegkopfs, "0x" + 16 Hexziffern (`backend/src/jtl/typen.ts`,
   * `JtlBestellung.bRowversion`) - Grundlage des optimistischen Sperrens bei
   * `jtlBestellkopfAendern` unten (Auftrag C2b). Anders als die uebrigen JTL-Kopffelder KEIN
   * Anzeigewert: er reist nur mit, damit ein spaeterer Schreibvorgang ihn unveraendert zurueckschicken
   * kann.
   */
  bRowversion: string;
  /**
   * Tag der LETZTEN Wareneinbuchung der Bestellung ("YYYY-MM-DD", Auftrag "Ware eingebucht mit Datum",
   * 2026-10-20) - `null`, wenn keine Einbuchung mit lesbarem Datum existiert. Nur in der LISTENantwort
   * (`GET /api/jtl/orders`); die Einzelbestellung traegt das Feld nicht (daher optional, siehe
   * `zusammenfuehren`).
   */
  letzteEinbuchungAm?: string | null;
    /**
     * Auftrag 06, E3 (Felder liefert Auftrag 05 im Backend): `true`, wenn die Bestellung in JTL
     * geloescht wurde. SNAKE_CASE ist hier ABSICHT - so heisst das Feld in der Serverantwort; eine
     * Umbenennung waere eine stille Fehlzuordnung. Wird nur durchgereicht (siehe `zusammenfuehren`).
     */
    deleted_in_jtl: boolean;
      /** Detail-/Mutations-Tieflink der Bestellung in JTL (`null`, wenn JTL keinen liefert). */
      detailUrl: string | null;
      mutationUrl: string | null;
      positionen: JtlPositionRoh[];
    }

    /** Die drei JTL-Loesch-/Linkfelder, die `zusammenfuehren` unveraendert in die `Order` durchreicht. */
    export interface JtlLoeschFelder {
      deleted_in_jtl: boolean;
      detailUrl: string | null;
      mutationUrl: string | null;
    }

/**
 * Das Praefix der abgeleiteten Kennung, EIN Ort statt eines dreifach verstreuten Literals (Fix
 * "Drawer einer Bestellung ohne Hub-Zeile wirft 500", 26.08.2026): stand vorher unabhaengig als
 * Literal in `abgeleiteteKennung` (Erzeuger), `istAbgeleiteteKennung` (Erkenner) und der Testfixtur
 * `verdrahtungZeitstrahl.test.tsx` - ein geaendertes Praefix haette den Filter der Sammelroute
 * (`zeitstrahlSendungenLaden`, `App.tsx`) stillschweigend wirkungslos gemacht, waehrend ALLE Tests
 * gruen geblieben waeren (Erzeuger und Erkenner haetten sich synchron mitgeaendert, die Testfixtur
 * kannte nur den abgeschriebenen String). Die Gegenprobe dazu: `abgeleiteteKennung`/
 * `istAbgeleiteteKennung` benutzen jetzt BEIDE diese Konstante, und die Testfixtur ruft
 * `abgeleiteteKennung(12345)` statt den String selbst abzuschreiben - ein geaendertes Praefix macht
 * damit zwangslaeufig mindestens einen Test rot.
 *
 * NICHT exportiert: Wer eine abgeleitete Kennung bauen, erkennen oder lesen will, ruft `abgeleiteteKennung`,
 * `istAbgeleiteteKennung` oder `nummerAusAbgeleiteterKennung` unten - EINE Stelle fuer Praefix und Muster. Eine
 * zweite Kopie waere genau die Vervielfaeltigung, die dieser Kommentar ausschliessen will; sie gab es schon einmal
 * (Befund G1: `components/App/App.tsx` fuehrte eine vierte Kopie, danach baute sich `bestellungsAufloesung.ts` ein
 * eigenes Muster aus der damals exportierten Konstante - beide sind weg, Fehleranzeige Paket 2, Task 14).
 */
const JTL_KENNUNG_PRAEFIX = "jtl-";

/** Die abgeleitete Kennung einer Bestellung, die (noch) keine Hub-Zeile hat. */
export function abgeleiteteKennung(kLieferantenBestellung: number): string {
  return `${JTL_KENNUNG_PRAEFIX}${kLieferantenBestellung}`;
}

/**
 * Ob `kennung` eine ABGELEITETE Kennung ist (`abgeleiteteKennung` oben), also KEINE echte Hub-`id` -
 * das benannte Gegenstueck, EIN Ort statt eines verstreuten `startsWith("jtl-")` (Fix "Sammelroute
 * lehnt den ganzen Aufruf ab, sobald eine Bestellung keine Hub-Zeile hat", 25.08.2026,
 * Produktionsbefund BE-10245). Eine Bestellung mit dieser Kennung KANN keine Verknuepfung zu einer
 * DSV-Sendung haben (`orders.id` gibt es fuer sie schlicht nicht), sie danach zu fragen ist sinnlos
 * und liess bislang `POST /api/orders/shipments` mit 400 fuer den GESAMTEN Aufruf scheitern
 * (`backend/src/http/routes/orderShipments.ts`).
 *
 * Dasselbe Muster wie `JTL_KENNUNG_MUSTER` in `backend/src/domaene/bestellungKennung.ts` - beide
 * MUESSEN zueinander passen, werden aber unabhaengig gefuehrt: Frontend und Backend teilen hier
 * keinen Code (siehe Kopfkommentar `zusammenfuehren` unten, "WELCHE SEITE GEWINNT" - dieselbe
 * Trennung gilt fuer den Modulgraphen).
 */
export function istAbgeleiteteKennung(kennung: string): boolean {
  return new RegExp(`^${JTL_KENNUNG_PRAEFIX}[1-9]\\d*$`).test(kennung);
}

/**
 * Die JTL-Nummer (`kLieferantenBestellung`) hinter einer ABGELEITETEN Kennung - die Umkehrung von
 * `abgeleiteteKennung`, neben dem Erkenner `istAbgeleiteteKennung` oben (Fehleranzeige Paket 2, Task 7b): EIN Ort
 * fuer Praefix und Muster. `null`, wenn `kennung` keine abgeleitete ist (eine UUID, eine eigene Bestellnummer) oder
 * die Nummer keine sichere ganze Zahl ist - ein gerundeter Wert wuerde an JTL eine ANDERE Bestellung adressieren.
 *
 * Gebraucht von `api/bestellungen.ts#bestellungLaden`: `GET /api/orders/jtl-<n>` antwortet fuer eine Bestellung ohne
 * Hub-Zeile mit `{}`, also ohne `jtlOrderId`; die Nummer fuer die Anreicherung steht dann nur in der Kennung. Und von
 * `components/App/bestellungsAufloesung.ts#bestellungNachKennungNachladen`, das eine abgeleitete Kennung aus der Adresse
 * ueber diese Nummer aus JTL laedt (`null` dort: kein Treffer).
 */
export function nummerAusAbgeleiteterKennung(kennung: string): number | null {
  if (!istAbgeleiteteKennung(kennung)) return null;
  const nummer = Number(kennung.slice(JTL_KENNUNG_PRAEFIX.length));
  return Number.isSafeInteger(nummer) ? nummer : null;
}

/**
 * Normalisiert eine SKU fuer den Vergleich "gleiche Ware" - WOERTLICH dieselbe Regel wie `skuKey`
 * in `lib/landedCost.ts:200`. `skuKey` ist dort aber NICHT der massgebliche Vergleichsschluessel:
 * `calcEk`s eigene Zwillingssuche zwischen einer FOC- und ihrer Hauptzeile vergleicht `baseSku`
 * (`landedCost.ts:215-216`) - `skuKey(...)` PLUS ein am Ende abgeschnittenes `-FOC`/`_FOC`/`FOC`-
 * Suffix (`replace(/(?:[-_]?FOC)$/i, "")`). `berechneFocVorbelegung` unten bildet deshalb BEIDE
 * Wege nach: erst den rohen Schluessel, dann - findet sich darueber kein bezahlter Zwilling - den
 * um das Suffix gekuerzten (siehe `baseSkuSchluessel` unten).
 *
 * Beide Stellen vergleichen SKUs INNERHALB derselben Bestellung und muessen deshalb auf DENSELBEN
 * Schluessel abbilden - laeuft diese Regel hier von `landedCost.ts:216` auseinander, haelt die eine
 * Stelle eine Position fuer "hat einen bezahlten Zwilling", waehrend `calcEk` sie gar nicht (oder an
 * eine andere Zeile) verrechnet, oder umgekehrt. Genau DAS sind die Fehlzuordnungen, die die vierte
 * Bedingung unten vermeiden soll - nur an einer neuen Stelle wieder eingefuehrt. Wer die eine Regel
 * aendert, zieht die andere im selben Schritt nach.
 */
function skuSchluessel(sku: string | null | undefined): string {
  return String(sku || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

/**
 * `skuSchluessel` PLUS das am Ende abgeschnittene FOC-Suffix - WOERTLICH dieselbe Regel wie
 * `baseSku` in `lib/landedCost.ts:216` (`skuKey(...).replace(/(?:[-_]?FOC)$/i, "")`). Faengt den
 * Fall, in dem die Gratiszeile selbst das Suffix traegt (`301015-FOC` neben bezahltem `301015`) -
 * der klarste FOC-Fall ueberhaupt, siehe `frontend/src/config/piProfiles.json:415-429`.
 */
function baseSkuSchluessel(sku: string | null | undefined): string {
  return skuSchluessel(sku).replace(/(?:[-_]?FOC)$/i, "");
}

/**
 * Die Menge normalisierter SKUs (`skuSchluessel`), die in EINER Bestellung mindestens einmal mit
 * einem Preis groesser 0 vorkommen - Grundlage der vierten Bedingung unten ("bezahlter Zwilling").
 * Wird EINMAL je Bestellung gebildet (`zusammenfuehren`), nicht je Position: eine Bildung je
 * Position waere quadratisch in der Anzahl der Positionen.
 *
 * BEWUSST NICHT AUSGESCHLOSSEN: eine Position mit `manual` gefuehrt (M-Kennzeichen) zaehlt hier
 * genauso als bezahlter Zwilling wie jede andere. `calcEk` (`landedCost.ts:204`) nimmt `manual`-
 * Zeilen dagegen ausdruecklich aus seiner Wertbasis (`mainSkuEntries`) heraus - waere die EINZIGE
 * Zeile mit demselben Schluessel und Preis > 0 in einer Bestellung `manual`, wuerde diese Funktion
 * sie hier als Zwilling zaehlen und die Gratiszeile vorbelegen, waehrend `calcEk` sie nicht als Ziel
 * findet und stattdessen auf den Vorgaenger-Fallback (`landedCost.ts:222-229`) auswiche - die
 * Gratismenge landete dann moeglicherweise auf einem fremden Artikel. Das setzt eine `manual`-
 * Extras-Zeile GENAU auf dem Zwilling voraus; im gesamten Produktivbestand (2026-08-18, 2138
 * Bestellungen) gibt es vier Extras-Zeilen ueberhaupt, keine davon `manual`. Wird `manual` haeufiger
 * gepflegt, ist das der Moment, diese Funktion um denselben Ausschluss zu ergaenzen.
 */
function bezahlteSkusJeBestellung(positionen: readonly JtlPositionRoh[]): ReadonlySet<string> {
  const bezahlt = new Set<string>();
  for (const position of positionen) {
    if ((position.ekNetto ?? 0) <= 0) continue;
    const schluessel = skuSchluessel(position.artikelNummer);
    if (schluessel) bezahlt.add(schluessel);
  }
  return bezahlt;
}

/**
 * Ob eine JTL-Position, die noch KEINE Hub-Zeile hat, mit FOC vorbelegt wird - Spec,
 * "Abgeleitete Klassifikationen": *„`fEKNetto = 0` aus JTL wird beim Einlesen als FOC-Kandidat
 * vorgeschlagen, aber nicht erzwungen"* (`docs/superpowers/specs/2026-08-04-selfhosted-jtl-multiuser-design.md:648`).
 *
 * Haengt AUSSCHLIESSLICH an `focKandidat`, NICHT an `price === 0`: `price` entsteht unten als
 * `position.ekNetto ?? 0` - eine Position mit UNGEPFLEGTEM EK (`NULL`) zeigt dort ebenfalls 0, ist
 * aber kein Gratisartikel, sondern ein vergessener Preis. `focKandidat` (Backend `istFocKandidat`,
 * `jtl/bestellungen.ts:222`) unterscheidet beides - `null` ist kein Kandidat, nur ausdruecklich
 * `fEKNetto = 0` ist es. Wer hier stattdessen den fertigen `price` prueft, macht aus jeder
 * Position mit vergessenem EK still eine Gratislieferung.
 *
 * Zwei Schutzregeln, weil auch DIESE mit EK 0 stehen, aber Warenwert 0 statt Gratislieferung
 * meinen: x-Artikel (`istManuellerArtikel`, Verpackungsmaterial/Beipack) und Positionen, die
 * bereits als M (`manual`) gefuehrt werden.
 *
 * ================= DIE VIERTE BEDINGUNG: BEZAHLTER ZWILLING (2026-08-18) =================
 *
 * Eine Nullpreis-Zeile wird NUR dann vorbelegt, wenn dieselbe Bestellung an anderer Stelle eine
 * Position MIT DERSELBEN SKU (`skuSchluessel`) und einem Preis groesser 0 fuehrt. Grund ist die
 * Legende in der Oberflaeche selbst: "FOC = kostenlose Ueberlieferung" setzt voraus, dass dieselbe
 * Ware auch bestellt und bezahlt wurde. Eine Gratiszeile ohne bezahlten Zwilling ist keine
 * Ueberlieferung, sondern ein Muster oder eine Beigabe - und damit kein FOC-Fall.
 *
 * Gemessen an den Produktivdaten (2026-08-18, 2138 Bestellungen): von 1036 Positionen mit EK 0
 * tragen 335 ein x-Praefix; von den verbleibenden 701 haben 534 einen bezahlten Zwilling, 167
 * nicht. Ohne diese Bedingung haengte `landedCost.ts:222-230` die Gratismenge der 167 an die
 * VORHERGEHENDE Nicht-FOC-Zeile - also an einen fremden Artikel, dessen Ø-EK dadurch faelschlich
 * sank -, und 31 Bestellungen aus ausschliesslich Nullpreis-Positionen liessen `mainItems`
 * (`landedCost.ts:202`) leer laufen.
 *
 * `bezahlteSkus` kommt FERTIG GEBILDET herein (`bezahlteSkusJeBestellung`, in `zusammenfuehren`
 * unten EINMAL je Bestellung gebildet) - siehe die Normalisierungs-Begruendung bei `skuSchluessel`
 * oben: sie MUSS zu `skuKey` in `lib/landedCost.ts:200` passen, sonst kehren die Fehlzuordnungen
 * an anderer Stelle zurueck.
 *
 * DIE SUCHE PROBIERT ZWEI SCHLUESSEL, GENAU WIE `calcEk`s ZWILLINGSSUCHE (2026-08-18): erst den
 * rohen `skuSchluessel` der Gratiszeile selbst - trifft das nicht, den um ein `-FOC`/`_FOC`/`FOC`-
 * Suffix gekuerzten `baseSkuSchluessel` (WOERTLICH `landedCost.ts:216`). Eine Gratiszeile `301015-
 * FOC` neben bezahltem `301015` findet ihren Zwilling so ueber den zweiten Schluessel, genau wie
 * `calcEk` sie dort verrechnet - ohne das Kuerzen faende diese Funktion keinen Zwilling und liesse
 * die Vorbelegung aus, waehrend `calcEk` die beiden Zeilen laengst zusammenfuehrt. DER ROHE WEG
 * BLEIBT ERHALTEN, das Kuerzen kommt nur DANACH hinzu: `bezahlteSkus` fuehrt die SKUs der bezahlten
 * Zeilen UNGEKUERZT (siehe `bezahlteSkusJeBestellung`) - traegt eine bezahlte Zeile selbst ein Suffix
 * wie "-FOC" in ihrer SKU (kein FOC-Marker, sondern Teil einer echten Artikelnummer), faende NUR der
 * rohe Vergleich ihren Zwilling; der gekuerzte Schluessel suchte dann faelschlich nach der um "FOC"
 * verkuerzten Form, die gar nicht bezahlt vorkommt. Das Kuerzen darf also Treffer nur HINZUFUEGEN,
 * nie welche wegnehmen - deshalb steht der rohe Nachschlag zuerst und bleibt bei jedem Treffer
 * massgeblich.
 *
 * Reine Arithmetik, als eigene Funktion exportiert und ohne DOM testbar - Vorbild `istFocKandidat`
 * im Backend.
 */
export function berechneFocVorbelegung(
  focKandidat: boolean,
  artikelNummer: string | null,
  manual: boolean,
  bezahlteSkus: ReadonlySet<string>,
): boolean {
  return (
    focKandidat &&
    !istManuellerArtikel(artikelNummer) &&
    !manual &&
    (bezahlteSkus.has(skuSchluessel(artikelNummer)) ||
      bezahlteSkus.has(baseSkuSchluessel(artikelNummer)))
  );
}

/**
 * Eine JTL-Position in der Form, die die Oberflaeche fuer `order.items` erwartet
 * (`sku`/`name`/`qty`/`price`, siehe `PositionsEditor#leerePosition` und `lib/landedCost.ts`),
 * angereichert um die tool-eigenen Felder DERSELBEN Position aus `order_item_extras`.
 *
 * `kArtikel` und `mwstProzent` reisen mit, obwohl das Board sie nicht anzeigt: die Anlage- und
 * Aenderungswege brauchen beide, und eine Position ohne `kArtikel` lehnt JTL als Freiposition ab.
 * Sie hier wegzulassen hiesse, sie beim naechsten Schreibvorgang neu beschaffen zu muessen.
 *
 * ================= WARUM `hub` GESPREIZT WIRD UND NICHT FELD FUER FELD STEHT =================
 *
 * `hub` ist eine Zeile aus `order_item_extras`, durch `oeffentlich()` in camelCase gebracht
 * (`domaene/items.ts#ladeItemsJeBestellung`) - also GENAU die tool-eigenen Positionsfelder und
 * nichts sonst. Eine ausgeschriebene Liste waere hier eine dritte Abschrift derselben Spalten
 * (nach `ITEM_FELDER` und `POSITIONS_FELDER`) und wuerde bei der naechsten Spalte still
 * unvollstaendig - dieselbe Begruendung, aus der `camelSchluessel` keine Nachschlagetabelle fuehrt.
 * `api/positionen.ts` bleibt davon unberuehrt: dort geht es um den SCHREIBWEG, und der muss die
 * beiden Schreibweisen ausdruecklich gegenueberstellen.
 *
 * Genau ZWEI Feldnamen fuehren beide Seiten: `foc` und `manual`. Sie stehen deshalb unten NACH der
 * Spreizung. JTL kennt keines von beiden als Spalte - aber `foc` bekommt einen VORSCHLAG aus
 * `focKandidat`, solange keine Hub-Zeile existiert (`berechneFocVorbelegung` oben). Existiert eine
 * Hub-Zeile, gewinnt ausschliesslich ihr `foc`-Wert, auch wenn er `false` ist: eine Extras-Zeile
 * IST die "schon entschieden"-Markierung, egal was in ihr steht.
 *
 * DER VORSCHLAG IST DERZEIT NICHT WIDERRUFBAR, solange die Bestellung keine lokale Zeile hat -
 * KEINE Zusicherung des Gegenteils: der Schreibweg (`Positionen.tsx:169-176#schreibwegVon`)
 * verlangt `jtlPosId` UND `rowVersion`, und beide stammen aus genau der Extras-Zeile, die in
 * diesem Fall fehlt. Der Rueckfall `onAendern(id, { items })` laeuft in `api/bestellungen.ts:517`
 * (`KEIN_FELDGRUPPEN_WEG`, Eintrag `items`) in `FeldOhneGruppeFehler`, BEVOR ueberhaupt gesendet
 * wird - ein bewusstes Abwaehlen bleibt also lautlos wirkungslos. Behoben wird das in einem
 * eigenen Schritt (Lazy-Anlage der Bestellzeile plus Upsert des Positionssatzes), NICHT hier.
 * `manual` bleibt ohne Hub-Zeile bei `false`, derselbe Vorgabewert wie in der Spalte.
 */
function alsOrderItem(
  position: JtlPositionRoh,
  bezahlteSkus: ReadonlySet<string>,
  neuerArtikelVorher: ReadonlyMap<number, boolean>,
  hub?: Record<string, unknown>,
): OrderItem {
  const manual = Boolean(hub?.["manual"]);
  return {
    ...hub,
    sku: position.artikelNummer ?? "",
    /**
     * DER ARTIKELNAME, seit dem 2026-08-16 aus JTL statt aus der Artikelnummer.
     *
     * Bis dahin stand hier `position.artikelNummer` - nicht aus Nachlaessigkeit, sondern weil die
     * Leseschicht des Backends gar keinen Namen holte (`jtl/bestellungen.ts`,
     * `BESTELLUNG_POSITIONEN_SPALTEN`). Sichtbar war das ueberall, wo `item.name` gezeichnet wird:
     * Bestell-Reiter, EK-Tabelle, Teil- und Sammellieferung, Board-Suche zeigten in der Spalte
     * "Artikel" eine Nummer. Das sah nach einer erfundenen Nummer aus und war keine - der Artikel
     * ist ueber `kArtikel` korrekt verknuepft, es wurde nur das falsche Feld angezeigt.
     *
     * KEIN RUECKFALL AUF DIE ARTIKELNUMMER bei fehlendem Namen: die Nummer steht bereits in `sku`,
     * also in ihrer eigenen Spalte unmittelbar daneben. Ein Rueckfall verdoppelte sie nur und
     * versteckte die Luecke wieder hinter etwas, das nach einem Namen aussieht.
     */
    name: position.name ?? "",
    qty: position.menge ?? 0,
    price: position.ekNetto ?? 0,
    foc:
      hub === undefined
        ? berechneFocVorbelegung(position.focKandidat, position.artikelNummer, manual, bezahlteSkus)
        : Boolean(hub["foc"]),
    manual,
    // Aus JTL (EK ausdruecklich 0), NICHT die Vorbelegung `foc` oben - siehe `OrderItem.focKandidat`.
    focKandidat: position.focKandidat,
    hinweis: position.hinweis ?? null,
    kArtikel: position.kArtikel,
    mwstProzent: position.mwstProzent,
    // Aus JTL, nicht aus dem Tool: wie viel bereits geliefert wurde und was offen ist.
    mengeGeliefert: position.mengeGeliefert,
    anzahlOffen: position.anzahlOffen,
    kLieferantenBestellungPos: position.kLieferantenBestellungPos,
    // Aus JTL, keine Hub-Entsprechung - siehe "WELCHE SEITE GEWINNT" oben.
    sortierung: position.sortierung,
    lieferantenBezeichnung: position.lieferantenBezeichnung,
    lieferantenArtNr: position.lieferantenArtNr,
    hatBild: position.hatBild,
    // Liste und Einzelbestellung liefern das Feld immer, die JTL-Schreibantworten nicht (Spec E3). Ein
    // gesendeter Wert gilt, auch `false`; sonst der Wert der bisherigen Position mit derselben `kArtikel`;
    // sonst `false`. Ausdruecklich gesetzt (nach `...hub`): ein nur mitgeschleppter Wert einer Hub-Zeile
    // entschiede still gegen JTL.
    neuerArtikel:
      typeof position.neuerArtikel === "boolean"
        ? position.neuerArtikel
        : typeof position.kArtikel === "number" && (neuerArtikelVorher.get(position.kArtikel) ?? false),
  };
}

/**
 * Fuehrt eine JTL-Bestellung mit ihrer Hub-Zeile zusammen. `hub` fehlt, wenn die Bestellung nur in
 * JTL steht.
 *
 * Die Reihenfolge im Rueckgabeobjekt ist die Aussage dieser Funktion: erst die Hub-Felder, dann die
 * JTL-Felder darueber. Wer das dreht, macht die Umstellung rueckgaengig, ohne dass ein Test es
 * zwangslaeufig merkt - deshalb steht es hier so ausdruecklich.
 */
export function zusammenfuehren(jtl: JtlBestellungRoh, hub?: Order): Order & JtlLoeschFelder {
  const hubPositionen = hubPositionenNachPosId(hub);
  // Einmal je Bestellung gebildet, nicht je Position - siehe `bezahlteSkusJeBestellung` oben.
  const bezahlteSkus = bezahlteSkusJeBestellung(jtl.positionen);
  // Ebenso einmal je Bestellung: der bisherige `neuerArtikel`-Stand je `kArtikel`, siehe `alsOrderItem`.
  const neuerArtikelVorher = neuerArtikelNachKArtikel(hub);
  return {
    ...hub,
    id: (hub?.id as string | undefined) ?? abgeleiteteKennung(jtl.kLieferantenBestellung),
    jtlOrderId: jtl.kLieferantenBestellung,
    kLieferant: jtl.kLieferant,
    // JTL vergibt die Bestellnummer erst NACH dem Speichern (Praesentation "HUB zu JTL", Folie 3:
    // "Bestellnummer wird erst nach dem Speichern generiert ... soll der Name einer Bestellung
    // sein"). Genau deshalb wird sie gelesen und nicht beim Anlegen einmalig uebernommen: sie
    // erscheint von selbst, sobald JTL sie vergeben hat.
    orderNo: jtl.eigeneBestellnummer ?? "",
    supplier: jtl.lieferantFirma,
    supplierId: jtl.kLieferant === null ? undefined : String(jtl.kLieferant),
    eur: jtl.waehrung === "EUR",
    waehrung: jtl.waehrung,
    invoiceNo: jtl.fremdbelegnummer ?? "",
    orderDate: jtl.erstellt,
    jtlLieferdatum: jtl.lieferdatum,
    jtlStatus: jtl.jtlStatus,
    jtlStatusLabel: jtl.jtlStatusLabel,
    bestaetigt: jtl.bestaetigt,
    // Aus JTL, keine Hub-Entsprechung - siehe "WELCHE SEITE GEWINNT" oben.
    kLager: jtl.kLager,
    lagerName: jtl.lagerName,
    bezugsAuftragsNummer: jtl.bezugsAuftragsNummer,
    internerKommentar: jtl.internerKommentar,
    deliveryTermText: jtl.deliveryTermText,
    faktor: jtl.faktor,
    bRowversion: jtl.bRowversion,
    // Liste und Einzelbestellung (`GET /api/jtl/orders[/:k]`) liefern das Feld immer, die
    // JTL-Schreibantworten nicht. Fehlt es, bleibt der Wert aus `...hub` stehen - App.tsx fuehrt eine
    // Schreibantwort mit der schon zusammengefuehrten Bestellung zusammen; ein unbedingtes
    // `jtl.letzteEinbuchungAm` loeschte ihn mit `undefined`. Ein gesendetes `null` gilt.
    ...(jtl.letzteEinbuchungAm === undefined ? {} : { letzteEinbuchungAm: jtl.letzteEinbuchungAm }),
        // Auftrag 06, E3: die drei Loesch-/Linkfelder aus Auftrag 05 - unveraendert durchgereicht, keine
        // eigene Logik (ob eine geloeschte Zeile gezeigt werden darf, entscheidet der Aufrufer bzw. der
        // Serverfilter ueber `state=deleted`).
        deleted_in_jtl: jtl.deleted_in_jtl,
        detailUrl: jtl.detailUrl,
        mutationUrl: jtl.mutationUrl,
        items: jtl.positionen.map((position) =>
      alsOrderItem(
        position,
        bezahlteSkus,
        neuerArtikelVorher,
        hubPositionen.get(position.kLieferantenBestellungPos),
      ),
    ),
  };
}

/**
 * `jtlPosId` -> Hub-Positionszeile. DIE Verbindung zwischen beiden Seiten: `order_item_extras` ist
 * ueber `(order_id, jtl_pos_id)` geschluesselt, und `jtl_pos_id` IST die
 * `kLieferantenBestellungPos` (`001_fundament.sql:153-156`). Ueber die SKU zu verbinden waere
 * falsch - JTL laesst dieselbe `cArtNr` zweimal in einer Bestellung zu, und genau daran laesst die
 * Migration eine Position lieber im Wartestand als sie still falsch zuzuordnen
 * (`migration/positionen.ts#lesePositionsZuordnung`).
 *
 * `Map`, kein Objektliteral: der Schluessel stammt aus einer Serverantwort, also aus Fremddaten -
 * dasselbe Muster wie `LESE_ZU_SCHREIBNAME` in `api/positionen.ts`.
 *
 * Eine Hub-Zeile OHNE Gegenstueck in JTL erscheint nirgends: die Liste der Positionen ist die aus
 * JTL, der Hub reichert nur an. Das ist keine Nachlaessigkeit, sondern die Regel des
 * Kopfkommentars - eine in JTL geloeschte Position ist geloescht, und ihre Extras-Zeile ist ein
 * Ueberbleibsel, keine Position.
 */
function hubPositionenNachPosId(hub?: Order): ReadonlyMap<number, Record<string, unknown>> {
  const nachPosId = new Map<number, Record<string, unknown>>();
  const positionen = Array.isArray(hub?.items) ? (hub.items as unknown[]) : [];
  for (const eintrag of positionen) {
    if (typeof eintrag !== "object" || eintrag === null) continue;
    const zeile = eintrag as Record<string, unknown>;
    const posId = Number(zeile["jtlPosId"]);
    if (Number.isSafeInteger(posId) && posId > 0) nachPosId.set(posId, zeile);
  }
  return nachPosId;
}

/**
 * `kArtikel` -> bisheriger `neuerArtikel`-Wert, aus den Positionen der bisherigen (schon zusammengefuehrten)
 * Bestellung. Nur fuer Schreibantworten, die das Feld nicht tragen (Spec E3): App.tsx fuehrt sie mit der
 * Bestellung im Speicher zusammen (`zusammenfuehren(neu, bestellung)`), deren `items` fertige `OrderItem`s sind.
 *
 * Bewusst NICHT ueber `hubPositionenNachPosId` gesucht: die schluesselt nach `jtlPosId`, und die fehlt an
 * Positionen ohne Hub-Zeile (reine JTL-Positionen) - dort ginge die Marke bei jeder Schreibantwort verloren.
 * `kArtikel` ist die Artikelkennung aus JTL und hat jede dieser Positionen; eine Position ohne Artikel
 * (`null`, 0) und unsichere Zahlen kommen nicht hinein, ebenso Eintraege ohne boolesches `neuerArtikel`.
 */
function neuerArtikelNachKArtikel(vorher?: Order): ReadonlyMap<number, boolean> {
  const nachKArtikel = new Map<number, boolean>();
  const positionen = Array.isArray(vorher?.items) ? (vorher.items as unknown[]) : [];
  for (const eintrag of positionen) {
    if (typeof eintrag !== "object" || eintrag === null) continue;
    const zeile = eintrag as Record<string, unknown>;
    const kArtikel = zeile["kArtikel"];
    const neu = zeile["neuerArtikel"];
    if (typeof kArtikel === "number" && Number.isSafeInteger(kArtikel) && kArtikel > 0 && typeof neu === "boolean") {
      nachKArtikel.set(kArtikel, neu);
    }
  }
  return nachKArtikel;
}

/** Die Zusammenfuehrung allein: JTL-Zeilen mit den Hub-Zeilen (Schluessel `jtlOrderId`). */
export function jtlBestellungenZusammenfuehren(
  bestellungen: readonly JtlBestellungRoh[],
  hubZeilen: readonly Order[],
): Array<Order & JtlLoeschFelder> {
  const hubNachJtlNummer = new Map<number, Order>();
  for (const zeile of hubZeilen) {
    const nummer = Number(zeile.jtlOrderId);
    if (Number.isSafeInteger(nummer) && nummer > 0) hubNachJtlNummer.set(nummer, zeile);
  }
  return bestellungen.map((jtl) =>
    zusammenfuehren(jtl, hubNachJtlNummer.get(jtl.kLieferantenBestellung)),
  );
}
